import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Popover } from '@mui/material';

import { TimeHelper } from '../../../utils';
import { CalendarDayOverviewProps, CalendarDayProps } from '../types';

import styles from './CalendarDay.module.scss';

function CalendarDayOverview({ items, limit }: CalendarDayOverviewProps) {
  const intl = useIntl();

  const elements = limit ? items.slice(0, limit) : items;

  if (!items.length) {
    return <div className={styles.overview_item}>{intl.formatMessage({ id: 'calendarOverview.noEvents' })}</div>;
  }

  return (
    <>
      { elements.map((item) => (
        <div
          key={`${item.isWalkin}-${item.labels.join('-')}`}
          className={clsx({
            [styles.overview_item]: true,
            [styles.error]: item.color === 'error',
          })}
        >
          <div className={styles.item_value}>
            <span>{item.value}{item.total && `/${item.total}`}</span>
            {!!item.overflow && (
              <span className={styles.overflow}>
                ({item.overflow})
              </span>
            )}
          </div>
          {item.labels.map((label) => intl.formatMessage({ id: label })).join('+')}
          {item.isWalkin ? ` ${intl.formatMessage({ id: 'appointment.walkin' })}` : ''}
        </div>
      )) }
    </>
  );
}

export function CalendarDay({ date, data, currentDate, timezone }: CalendarDayProps) {
  const { overview = [], weekEnd = false } = data;

  const intl = useIntl();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const open = Boolean(anchorEl);

  const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const { currentTarget } = event;

    if (!weekEnd) {
      timeoutRef.current = setTimeout(() => {
        setAnchorEl(currentTarget);
      }, 100);
    }
  }, []);

  const handlePopoverClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setAnchorEl(null);
  }, []);

  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const isToday = useMemo(
    () => TimeHelper.unixToStandardFormat(date, timezone) === TimeHelper.unixToStandardFormat(currentDate, timezone),
    [date, currentDate, timezone],
  );

  const isPast = useMemo(
    () => TimeHelper.isBeforeDay(date, currentDate, timezone),
    [date, currentDate, timezone],
  );

  return (
    <div
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      className={clsx({
        [styles.today]: isToday,
        [styles.day_card]: true,
        [styles.active]: !isPast && overview.length && !weekEnd,
      })}
    >
      {data.hasOverbookingSlots && (
        <div className={clsx(styles.dotIndicator, styles.accent)} />
      )}

      <div className={styles.title}>{TimeHelper.toDayAndMonthFormat(date, timezone)}</div>
      <div className={styles.title_addon}>
        {isToday && <span className={styles.today_label}>{intl.formatMessage({ id: 'common.today' })}</span>}
      </div>
      {!weekEnd && <CalendarDayOverview items={overview} limit={3} />}

      {overview.length > 0 && (
        <Popover
          id={`mouse-over-popover-${TimeHelper.unixToStandardFormat(date, timezone)}`}
          sx={{ pointerEvents: 'none' }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
          disableEnforceFocus
          disableAutoFocus
          slotProps={{
            paper: {
              style: {
                margin: '0 8px',
              },
            },
          }}
        >
          <div className={styles.detailed_overview}>
            {data.hasOverbookingSlots && (
              <div className={clsx(styles.dotIndicator, styles.accent)} />
            )}
            <div className={styles.title}>{TimeHelper.toDayAndMonthFormat(date, timezone)}</div>
            <div className={styles.overview_container}>
              <CalendarDayOverview items={overview} />
            </div>
          </div>
        </Popover>
      )}
    </div>
  );
}
